import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PortalAuthorizations from "@/components/PortalAuthorizations";
import PortalEvidence from "@/components/PortalEvidence";
import { Anchor, Loader2, AlertTriangle, CalendarClock, ShieldCheck, LogOut, History, GitCompare, FileText } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SUMMARY_STYLE = {
  normal: "text-emerald-700 bg-emerald-50",
  attention: "text-amber-700 bg-amber-50",
  urgent: "text-red-700 bg-red-50",
  not_observed: "text-slate-600 bg-slate-100",
};

export default function PortalHome() {
  const navigate = useNavigate();
  const [vessels, setVessels] = useState(null);
  const [home, setHome] = useState(null);
  const [loadingHome, setLoadingHome] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [compare, setCompare] = useState(null);

  useEffect(() => {
    api
      .get("/portal/vessels")
      .then(({ data }) => {
        setVessels(data);
        if (data.length) select(data[0].id);
      })
      .catch((e) => navigate(e.response?.status === 404 ? "/pricing" : "/portal/login", { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const select = (id) => {
    setLoadingHome(true);
    setCompare(null);
    setTimeline([]);
    api
      .get(`/portal/vessels/${id}`)
      .then(({ data }) => {
        setHome(data);
        const iid = data.last_completed_visit?.inspection_id;
        api.get(`/portal/vessels/${id}/timeline`).then(({ data: t }) => setTimeline(t.events || [])).catch(() => {});
        if (iid) {
          api.get(`/portal/inspections/${iid}/comparison?against=previous`).then(({ data: c }) => setCompare(c)).catch(() => {});
        }
      })
      .finally(() => setLoadingHome(false));
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    navigate("/portal/login", { replace: true });
  };

  if (vessels === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" />
      </div>
    );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-home-page">
      <header className="bg-[#051329] text-white px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[#D4AF37] tracking-[0.25em] text-xs">
          <Anchor className="h-4 w-4" /> HMC OWNER PORTAL
        </div>
        <button data-testid="portal-logout" onClick={logout} className="text-slate-300 text-sm flex items-center gap-1">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {vessels.length === 0 && (
          <p data-testid="portal-no-vessels" className="text-slate-600">No vessels are associated with your account yet.</p>
        )}

        {vessels.length > 1 && (
          <div className="flex gap-2 mb-5 overflow-x-auto" data-testid="portal-vessel-switcher">
            {vessels.map((v) => (
              <button
                key={v.id}
                data-testid={`portal-vessel-tab-${v.id}`}
                onClick={() => select(v.id)}
                className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap border ${
                  home?.vessel?.id === v.id ? "bg-[#051329] text-white border-[#051329]" : "bg-white text-slate-700 border-slate-200"
                }`}
              >
                {v.name}
              </button>
            ))}
          </div>
        )}

        {loadingHome && (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" /></div>
        )}

        {home && !loadingHome && (
          <div data-testid="portal-vessel-home" className="space-y-5">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-slate-100">
              {home.photo_id && (
                <img
                  data-testid="portal-vessel-photo"
                  alt={home.vessel.name}
                  src={`${BACKEND}/api/portal/photos/${home.photo_id}`}
                  className="w-full h-44 object-cover"
                />
              )}
              <div className="p-5">
                <h1 data-testid="portal-vessel-name" className="text-xl font-semibold text-[#051329]">{home.vessel.name}</h1>
                <p className="text-sm text-slate-500 mt-1">
                  {home.vessel.make} {home.vessel.model} {home.vessel.year} ·{" "}
                  <span data-testid="portal-berth-type">
                    {home.vessel.berth_type === "private_dock_lift" ? "Private dock & lift" : "Marina slip"}
                  </span>
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-xl p-4 border border-slate-100" data-testid="portal-last-visit">
                <div className="text-xs text-slate-500 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Last observed</div>
                <div className="text-sm font-medium text-[#051329] mt-1">
                  {home.last_completed_visit ? home.last_completed_visit.completed_at?.slice(0, 10) : "No completed visit yet"}
                </div>
                {home.inspector_display_name && (
                  <div className="text-xs text-slate-500 mt-1">by {home.inspector_display_name}</div>
                )}
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-100" data-testid="portal-next-visit">
                <div className="text-xs text-slate-500 flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Next scheduled</div>
                <div className="text-sm font-medium text-[#051329] mt-1">
                  {home.next_scheduled_visit ? home.next_scheduled_visit.scheduled_date : "Not scheduled"}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 border border-slate-100" data-testid="portal-observation-summary">
              <div className="text-sm font-medium text-[#051329] mb-3">Current observation summary</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(home.observation_summary).map(([k, n]) => (
                  <span key={k} className={`text-xs px-2.5 py-1 rounded-full ${SUMMARY_STYLE[k] || "bg-slate-100"}`}>
                    {k.replace("_", " ")}: {n}
                  </span>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-3">{home.semantics_note}</p>
            </div>

            <div className="bg-white rounded-xl p-4 border border-slate-100" data-testid="portal-attention-items">
              <div className="text-sm font-medium text-[#051329] mb-2 flex items-center gap-1">
                <AlertTriangle className="h-4 w-4 text-amber-500" /> Needs attention
              </div>
              {home.open_attention_items.length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="portal-no-attention">No open attention or urgent items.</p>
              ) : (
                <ul className="space-y-2">
                  {home.open_attention_items.map((it) => (
                    <li key={it.id} data-testid={`portal-attention-${it.id}`} className="text-sm">
                      <span className={`font-semibold ${it.severity === "URGENT" ? "text-red-600" : "text-amber-600"}`}>{it.severity}</span>
                      {" — "}{it.summary}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {home.pending_authorizations > 0 && (
              <div data-testid="portal-pending-auth" className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                {home.pending_authorizations} action(s) awaiting your authorization.
              </div>
            )}

            <PortalAuthorizations vesselId={home.vessel.id} onDecided={() => select(home.vessel.id)} />

            <PortalEvidence vesselId={home.vessel.id} />

            {home.last_completed_visit && (
              <div className="flex gap-2" data-testid="portal-report-actions">
                <a
                  data-testid="portal-view-report"
                  href={`${BACKEND}/api/portal/inspections/${home.last_completed_visit.inspection_id}/report`}
                  target="_blank" rel="noreferrer"
                  className="flex-1 text-center rounded-lg border border-slate-200 bg-white py-2.5 text-sm text-[#051329] flex items-center justify-center gap-1"
                >
                  <FileText className="h-4 w-4" /> View report
                </a>
                <a
                  data-testid="portal-download-pdf"
                  href={`${BACKEND}/api/portal/inspections/${home.last_completed_visit.inspection_id}/report.pdf`}
                  target="_blank" rel="noreferrer"
                  className="flex-1 text-center rounded-lg bg-[#051329] py-2.5 text-sm text-white flex items-center justify-center gap-1"
                >
                  <FileText className="h-4 w-4" /> PDF
                </a>
              </div>
            )}

            {compare && (
              <div className="bg-white rounded-xl p-4 border border-slate-100" data-testid="portal-comparison">
                <div className="text-sm font-medium text-[#051329] mb-1 flex items-center gap-1">
                  <GitCompare className="h-4 w-4" /> Since previous completed visit
                </div>
                {compare.prior_inspection_id ? (
                  <>
                    <p className="text-xs text-slate-500 mb-2">Compared with {compare.prior_completed_at?.slice(0, 10)}.</p>
                    <ul className="space-y-1.5">
                      {compare.deltas
                        .filter((d) => ["CLASSIFICATION_CHANGED", "FIRST_OBSERVED", "NEWLY_CLASSIFIED"].includes(d.change_type))
                        .map((d) => (
                          <li key={d.component_key} data-testid={`portal-delta-${d.component_key}`} className="text-sm">
                            <b>{d.label}</b>:{" "}
                            {d.change_type === "CLASSIFICATION_CHANGED"
                              ? `Recorded classification changed from ${d.from} to ${d.to}.`
                              : d.change_type === "FIRST_OBSERVED"
                              ? `First recorded observation this visit: ${d.to}.`
                              : `Recorded classification set to ${d.to} (previously not observed).`}
                          </li>
                        ))}
                    </ul>
                    {compare.coverage_gap_keys?.length > 0 && (
                      <p data-testid="portal-coverage-gaps" className="text-xs text-amber-700 mt-2">
                        Coverage gaps: {compare.coverage_gap_keys.join(", ")} (not observed — not a finding of normal).
                      </p>
                    )}
                    <p className="text-[11px] text-slate-400 mt-2">{compare.disclaimer}</p>
                  </>
                ) : (
                  <p className="text-sm text-slate-500" data-testid="portal-no-comparison">{compare.note}</p>
                )}
              </div>
            )}

            <div className="bg-white rounded-xl p-4 border border-slate-100" data-testid="portal-timeline">
              <div className="text-sm font-medium text-[#051329] mb-3 flex items-center gap-1">
                <History className="h-4 w-4" /> Vessel history
              </div>
              {timeline.length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="portal-timeline-empty">No history yet.</p>
              ) : (
                <ol className="relative border-l border-slate-200 ml-1.5 space-y-4">
                  {timeline.slice(0, 40).map((ev, idx) => (
                    <li key={idx} data-testid={`portal-timeline-item-${idx}`} className="ml-4">
                      <div className="absolute -left-1.5 w-3 h-3 rounded-full bg-[#D4AF37]" />
                      <div className="text-xs text-slate-400">{ev.at?.slice(0, 10)}</div>
                      <div className="text-sm text-[#051329]">{ev.title}</div>
                      {ev.summary && <div className="text-xs text-slate-500">{ev.summary}</div>}
                      {ev.contractor_reference && (
                        <div className="text-xs text-slate-500">{ev.contractor_reference}{ev.purpose_scope ? ` — ${ev.purpose_scope}` : ""}</div>
                      )}
                      {ev.resolution_type && <div className="text-xs text-slate-500">Resolution: {ev.resolution_type}</div>}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
